import React, { useState, useEffect } from "react";
import { Search, Brain, Loader2, Sparkles, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import Navbar from "../../components/Navbar";
import { useAuth } from "../../context/studentAuthContext";
import { Button } from "@mui/material";
import { ArrowForward } from "@mui/icons-material";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function RecommendationResults() {
  const { student: authStudent } = useAuth();
  const [interests, setInterests] = useState("");
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [matchingStep, setMatchingStep] = useState("");

  const student = authStudent?.id
    ? authStudent
    : (() => {
        try {
          const raw = localStorage.getItem("student");
          return raw ? JSON.parse(raw) : null;
        } catch (_err) {
          return null;
        }
      })();

  useEffect(() => {
    const loadInitialRecommendations = async () => {
      let derivedInterest = "Software Engineering and Web Development";
      if (student?.id) {
        try {
          const profileRes = await axios.get(`${BASE_URL}/api/students/profile/${student.id}`);
          const prefRaw = profileRes.data?.preference;
          if (prefRaw) {
            try {
              const parsed = JSON.parse(prefRaw);
              if (Array.isArray(parsed) && parsed.length) {
                derivedInterest = parsed.join(", ");
              }
            } catch (_e) {
              if (String(prefRaw).trim()) {
                derivedInterest = String(prefRaw).trim();
              }
            }
          }
        } catch (err) {
          console.error("Failed to load profile for initial recs:", err);
        }
      }
      setInterests(derivedInterest);
      await fetchRecommendations(derivedInterest);
    };

    loadInitialRecommendations();
  }, [student?.id]);

  const fetchRecommendations = async (query = "") => {
    if (!query.trim()) return;
    setLoading(true);
    setSearched(true);
    
    // UI steps for the "AI Thinking" state
    const steps = ["Analyzing goals...", "Searching curriculum...", "Expert matching...", "Ranking by relevance..."];
    let stepIdx = 0;
    const stepInterval = setInterval(() => {
      setMatchingStep(steps[stepIdx % steps.length]);
      stepIdx++;
    }, 1500);

    try {
      // 1. Get AI Semantic Recommendations
      const res = await axios.post(`${BASE_URL}/api/ai/recommendations`, {
        studentInterests: query,
        studentId: student?.id || 1
      });

      const aiRecs = res.data?.recommendations || [];
      
      if (aiRecs.length > 0) {
        const approvedRes = await axios.get(`${BASE_URL}/api/students/approved-courses`);
        const allApproved = approvedRes.data?.data || [];
        
        const enriched = aiRecs.map(rec => {
          const original = allApproved.find(c => c.id === rec.id) || {};
          return { ...rec, ...original, aiReason: rec.fit, aiScore: rec.score };
        }).filter(c => c.course_name);

        setCourses(enriched);
      } else {
        setCourses([]);
      }
    } catch (err) {
      console.error("AI Recommendation failed:", err);
      setCourses([]);
    } finally {
      clearInterval(stepInterval);
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRecommendations(interests);
  };

  return (
    <>
      <Navbar/>
      <div className="mt-20 lg:mt-20 min-h-screen bg-[#f8f9fb] p-4 md:p-8 font-sans antialiased text-slate-900">
        <div className="max-w-6xl mx-auto">
          
          {/* AI Header with Search - Restored Original Style */}
          <div className="mb-12 bg-white p-8 rounded-2xl shadow-sm border border-slate-100">
            <div className="flex items-center gap-3 mb-4">
               <div className="p-2 bg-blue-600 rounded-lg text-white">
                  <Brain className="w-6 h-6" />
               </div>
               <h1 className="text-2xl md:text-3xl font-black tracking-tight">AI Career Path Finder</h1>
            </div>
            <p className="text-slate-500 font-medium mb-8">Tell us what you want to learn and we will match you with approved tutor courses using AI.</p>


            <form onSubmit={handleSearch} className="relative max-w-2xl">
              <input 
                type="text" 
                value={interests}
                onChange={(e) => setInterests(e.target.value)}
                placeholder="e.g. I want to build mobile apps with React Native or learn Ethical Hacking"
                className="w-full pl-12 pr-32 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 outline-none transition-all font-medium text-slate-700"
              />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
              <button 
                type="submit"
                disabled={loading}
                className="absolute right-2 top-2 bottom-2 bg-blue-600 hover:bg-slate-900 text-white px-6 rounded-xl font-bold text-xs uppercase tracking-widest transition-all flex items-center gap-2"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="hidden sm:inline text-[9px]">{matchingStep}</span>
                  </div>
                ) : "Discover"}
              </button>
            </form>
          </div>

          {searched && (
            <div className="mb-8 flex items-center justify-between">
               <h2 className="text-sm font-black uppercase tracking-widest text-slate-400">Recommended for you</h2>
               <div className="flex items-center gap-2">
                 <Sparkles className="w-4 h-4 text-blue-600" />
                 <span className="text-xs font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">{courses.length} Matches Found</span>
               </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pb-12">
            {loading ? (
                // Skeleton UI
                [...Array(3)].map((_, i) => (
                   <div key={i} className="bg-white h-[450px] rounded-2xl animate-pulse border border-slate-100"></div>
                ))
            ) : courses.map((course) => (
                <div key={course.id} className="group bg-white flex flex-col h-full rounded-2xl overflow-hidden border border-slate-200 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
                  <div className="relative aspect-video overflow-hidden bg-slate-100">
                    <img
                      src={
                        course.profile_img
                          ? `${BASE_URL}/uploads/tutors/${course.profile_img}`
                          : `https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&q=80`
                      }
                      alt={course.course_name || "Tutor course"}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute top-3 left-3 flex gap-2">
                      <span className="bg-blue-600 text-white text-[10px] font-black px-3 py-1 rounded-full shadow-sm flex items-center gap-1 uppercase tracking-tighter">
                         {course.aiScore || 85}% Match
                      </span>
                    </div>
                  </div>

                  <div className="p-6 flex flex-col flex-grow">
                    <div className="mb-4">
                        <span className="text-[10px] font-black uppercase text-blue-600 tracking-widest px-2 py-0.5 bg-blue-50 rounded italic">{course.category || "General"}</span>
                        <h3 className="text-lg font-bold text-slate-900 leading-snug mt-2 group-hover:text-blue-600 transition-colors">
                            {course.course_name}
                        </h3>
                        <p className="text-xs text-slate-500 mt-1">Tutor: {course.tutor_name || "-"}</p>
                    </div>

                    <div className="bg-blue-50/50 border border-blue-100 p-4 rounded-xl mb-6 relative overflow-hidden">
                        <div className="absolute right-[-8px] top-[-8px] opacity-10">
                            <Zap className="w-12 h-12 text-blue-900" />
                        </div>
                        <p className="text-[11px] text-slate-600 leading-relaxed italic font-medium relative z-10">
                            "{course.aiReason || course.description || "Explore this tutor course and enroll to start learning."}"
                        </p>
                    </div>

                    <div className="mt-auto pt-4 border-t border-slate-100 flex flex-col gap-3">
                      <Button
                        variant="contained"
                        component={Link}
                        to={`/tutor-details/${course.id}`}
                        endIcon={<ArrowForward />}
                        fullWidth
                        sx={{
                          borderRadius: 3,
                          backgroundColor: "#1976D2",
                          fontWeight: 700,
                          textTransform: 'none'
                        }}
                      >
                        Enroll Now
                      </Button>
                    </div>
                  </div>
                </div>
            ))}
          </div>

          {!loading && searched && courses.length === 0 && (
            <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-slate-200">
               <p className="text-slate-400 font-bold">No tutor courses matched your career goal. Try a broader search.</p>
            </div>
          )}
        </div>
      </div>

      {/* Localized Paystack Button Style to avoid external CSS network error */}
      <style jsx="true">{`
        .paystack-btn-fix {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          background: #3bb75e !important;
          color: white !important;
          padding: 12px 24px !important;
          border-radius: 8px !important;
          font-weight: bold !important;
          border: none !important;
          cursor: pointer !important;
        }
      `}</style>
    </>
  );
}